import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { RootState, useAppDispatch, useAppSelector } from '../store';
import { mcpService } from '../Services/mcpService';
import toast from 'react-hot-toast';
import { PaginationListType } from '../Types/PaginationParams';
import { setListReloadType } from '../Store/features/environment/environmentSlice';
import useModal from '../components/Modal/useModal';
import { getBreakpoint } from '../utils/breakpoint';

interface McpConfig {
    identifier: string;
    title: string;
    description: string;
    serverUrl: string;
    transport: string;
    authType: string;
    lastSync: string | null;
    lastError: string | null;
    toolsCount: number;
}

const useMcpConfigs = () => {
    const navigate = useNavigate();
    const i18n = useAppSelector((state: RootState) => state.i18n.i18n);
    const dispatch = useAppDispatch();
    const windowWidth = useAppSelector((state: RootState) => state.environment.windowWidth);
    const settings = useAppSelector((state: RootState) => state.settings);
    const modal = useModal();
    const handleCreateNew = () => {
        navigate('/admin/mcpconfig/new');
    };

    const handleEdit = (config: McpConfig) => {
        navigate(`/admin/mcpconfig/${config.identifier}`);
    };

    const handleDelete = useCallback(
        async (identifier: string) => {
            try {
                await mcpService.deleteConfig(identifier);
                dispatch(setListReloadType(PaginationListType.McpConfigs));
                toast.success(i18n.translate({ id: 'config.delete.success', sourceName: 'MCP' }));
            } catch (error) {
                console.error('Failed to delete MCP config:', error);
                toast.error(i18n.translate({ id: 'config.delete.error', sourceName: 'MCP' }));
            }
        },
        [dispatch, i18n],
    );

    const handleOpenConfirmDialog = useCallback(
        (identifier: string, title: string) => {
            modal.deleteConfirm({
                title: i18n.translate({ id: 'config.delete.title', sourceName: 'MCP' }),
                content: i18n.translate({
                    id: 'config.delete.text',
                    sourceName: 'MCP',
                    params: {
                        title: title,
                    },
                }),
                onConfirm: () => handleDelete(identifier),
            });
        },
        [modal, i18n, handleDelete],
    );

    return {
        settings,
        handleOpenConfirmDialog,
        i18n,
        windowWidth,
        getBreakpoint,
        handleEdit,
        handleCreateNew,
    };
};

export default useMcpConfigs;
